import { useCallback } from 'react';

export interface ViewLessonPreview {
  isViewLesson: boolean;
  isAnswerMode: boolean;
  isSolutionMode: boolean;
  isPreview: boolean;
  guardSave: <T extends (...args: any[]) => any>(fn: T) => T;
  handleDoneClick: (studentOnDone: () => void) => void;
}

/**
 * Single source of truth for the sysadmin EL_ViewLesson preview flow
 * (QNS / QNS-ANS / SOLUTION / RFC buttons -> Question page).
 * Every *Qns.tsx component calls this instead of re-deriving
 * isViewLesson/isAnswerMode/isSolutionMode/isPreview by hand.
 */
export function useViewLessonPreview(previewMode?: string | null): ViewLessonPreview {
  const params = new URLSearchParams(window.location.search);

  const isViewLesson = params.get("ViewLesson") === "Y";
  const isAnswerMode = previewMode === "ANSWERS" || previewMode === "SOLUTION";
  const isSolutionMode = previewMode === "SOLUTION";
  const isPreview = isViewLesson && (isAnswerMode || isSolutionMode);

  const guardSave = useCallback(<T extends (...args: any[]) => any>(fn: T): T => {
    if (!isPreview) return fn;
    return ((..._args: any[]) => Promise.resolve(null)) as T;
  }, [isPreview]);

  const handleDoneClick = useCallback((studentOnDone: () => void) => {
    if (isViewLesson) {
      const calledFrom = params.get("CalledFrom") || params.get("calledFrom");
      if (calledFrom) {
        window.location.href = decodeURIComponent(calledFrom);
      } else if (window.history.length > 1) {
        window.history.back();
      }
      return;
    }
    studentOnDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewLesson]);

  return { isViewLesson, isAnswerMode, isSolutionMode, isPreview, guardSave, handleDoneClick };
}